import identity from 'lodash/identity';
import omit from 'lodash/omit';
import { AttributionMode, DataScienceOptimization, MessageMedium } from 'modules/domains/campaign';

import {
  createCustomConversions,
  generateUpdateCampaignPayload,
  GenerateUpdateCampaignPayloadOptions,
} from 'modules/campaigns/lib/hooks/useGenerateSaveCampaignPayload';

import { GraphQLCampaign } from 'modules/campaigns/lib/types';
import { CampaignEditorFormState, CampaignStep } from '../../types';

export type CampaignStepHydrateFunction = (
  values: CampaignEditorFormState,
  metaData: Record<string, any>,
) => Record<string, any>;

const saveConversionStep = (values: CampaignEditorFormState) => {
  const {
    conversions: { events, periodHours, mode },
  } = values;
  return createCustomConversions(events, periodHours, mode as AttributionMode);
};

const saveDeliverySteps = (
  values: CampaignEditorFormState,
  { projectTimeZone, messageMedium, existingSchedulerMsgParams }: Record<string, any>,
) => {
  const customRateLimit =
    values.useRateLimit && values?.rateLimit?.rateLimitValue
      ? {
          useRateLimit: true,
          rateLimitPerMin:
            values?.rateLimit?.rateLimitUnit === 'second'
              ? parseInt(values?.rateLimit?.rateLimitValue, 10) * 60
              : parseInt(values?.rateLimit?.rateLimitValue, 10),
        }
      : null;
  const dataScienceOptimizations = values.useSendTimeOptimization
    ? [DataScienceOptimization.SendTimeOptimization]
    : [];

  /**
   * This is pulled from the BE database when an empty array is sent,
   * so we don't need to send back the actual value.
   * @see https://iterable.slack.com/archives/C04B934NDJ5/p1682466591640639
   * @see https://github.com/Iterable/Iterable/pull/17407
   */
  const schedulerMsgIds: Array<0> = [];

  // initialize updated schedulerMsgParams with whatever saved values there are
  let schedulerMsgParams = existingSchedulerMsgParams;

  // keep existing scheduler values if...
  // 1) STO was previously activated and is still active
  // 2) another delivery setting was updated with no changes to STO

  const wasStoPreviouslyActivated =
    existingSchedulerMsgParams?.scheduleType === DataScienceOptimization.SendTimeOptimization ||
    existingSchedulerMsgParams?.automaticSendTime;
  const isStoCurrentlyActivated = values.useSendTimeOptimization;

  if (wasStoPreviouslyActivated) {
    // this means STO was saved previously

    if (isStoCurrentlyActivated) {
      /**
       * POSSIBLE SCENARIO: STO time range is changed
       * so copy over most recent values for `windowHours`
       * and set the `automaticSendTime` field to `true`
       */
      schedulerMsgParams = {
        ...schedulerMsgParams,
        windowHours: Number(values.stoCompletionTime),
        automaticSendTime: true,
      };
    } else {
      /**
       * SCENARIO: STO is turned from on to off
       * following Angular logic where `windowHours` is just removed from payload,
       * and we need to set the `automaticSendTime` field to `false`
       */
      const partialSchedulerMsgParams = omit(schedulerMsgParams, 'windowHours');
      schedulerMsgParams = { ...partialSchedulerMsgParams, automaticSendTime: false };
    }
  } else if (!wasStoPreviouslyActivated && isStoCurrentlyActivated) {
    // this means STO is newly activated

    schedulerMsgParams = {
      // start with any existing values that may exist
      // from the campaign being scheduled
      ...schedulerMsgParams,

      // use saved values for time zones if they exist
      // from a previous schedule
      // or otherwise default to project time zone
      defaultTimeZone: schedulerMsgParams?.defaultTimeZone || projectTimeZone,
      startTimeZone: schedulerMsgParams?.startTimeZone || projectTimeZone,

      scheduleType: DataScienceOptimization.SendTimeOptimization,
      schedulerMsgIds,
      windowHours: Number(values.stoCompletionTime),
    };
  }

  const customQuietHours = values.useQuietHours
    ? {
        startTime: values?.customQuietHours?.startTime,
        endTime: values?.customQuietHours?.endTime,
        sendSkip: values?.customQuietHours?.sendSkip,
      }
    : null;

  const campaignAlertsEnabled = !!values?.campaignAlertsEnabled;

  return {
    customQuietHours,
    customRateLimit,
    dataScienceOptimizations,
    ignoreFrequencyCap: Boolean(values.ignoreFrequencyCap),
    schedulerMsgParams,
    priorityLevel: messageMedium === MessageMedium.InApp ? values?.priorityLevel : null, // only add priority from the form with an InApp type
    campaignAlertsEnabled,
  };
};

const CAMPAIGN_HYDRATE_FUNCTIONS: Record<CampaignStep, CampaignStepHydrateFunction> = {
  [CampaignStep.Delivery]: saveDeliverySteps,
  [CampaignStep.ConversionAndExperiment]: saveConversionStep,
  [CampaignStep.Conversions]: saveConversionStep,
  [CampaignStep.Audience]: identity,
  [CampaignStep.Display]: identity,

  /** These views/steps are not currently editable */
  [CampaignStep.Content]: identity,
  [CampaignStep.Summary]: identity,
  [CampaignStep.AudienceTriggered]: identity,
  [CampaignStep.AudienceEligibility]: identity,
  [CampaignStep.AlertSettings]: identity,
};

export interface GetSaveCampaignPayloadOptions extends GenerateUpdateCampaignPayloadOptions {
  currentEditableStep?: CampaignStep;
}

export const getSaveCampaignPayload = (
  campaign: GraphQLCampaign,
  formValues: CampaignEditorFormState,
  options: GetSaveCampaignPayloadOptions,
) => {
  const { projectTimeZone, currentEditableStep } = options;
  const { messageMedium, schedulerMsgParams: existingSchedulerMsgParams } = campaign;

  const getUpdatedFields = currentEditableStep
    ? CAMPAIGN_HYDRATE_FUNCTIONS[currentEditableStep]
    : identity;
  const metaData = { projectTimeZone, messageMedium, existingSchedulerMsgParams };
  const updatedFields = getUpdatedFields?.(formValues, metaData) || {};

  return generateUpdateCampaignPayload(campaign, updatedFields, options);
};
